<template>
    <div class="variations-wrapper">
        <div v-for="(attributeGroup, index) in productAttributes" :key="index" 
            :class="[
            'variations',
            { 'pt_check_hidden': attributeGroup?.length === 1 &&(attributeGroup[0]?.attribute == 'gold-weight' || attributeGroup[0]?.attribute == 'goldweight')},
            {'all_oos':engravingDesignStatus === 'outofstock'}
            ]">
            <div class="label">{{ attributeGroup?.[0]?.name }}</div>
            <select
                :id="attributeGroup?.[0]?.attribute"
                :name="`attribute_${attributeGroup?.[0]?.attribute}`"
                :data-attribute_name="`attribute_${attributeGroup?.[0]?.attribute}`"
                v-model="mutableSelectedOptions[attributeGroup?.[0]?.attribute]"
                @change="onOptionChange(attributeGroup?.[0]?.attribute)"
                required
            >
                <option value="">Choose an option</option>
                <option v-for="(attribute, idx) in attributeGroup" :key="idx" :value="attribute?.slug">
                    {{ attribute?.value }}
                </option>
            </select>

            <ul :class="['jos_variation', 'var-' + (attributeGroup?.[0]?.attribute || '')]" :data-att="attributeGroup?.[0]?.attribute">
                <li
                    v-for="(attribute, idx) in attributeGroup"
                    :key="idx"
                    :data-key="attribute?.slug"
                    :class="['item_exits', {
                selected: mutableSelectedOptions?.[attributeGroup?.[0]?.attribute ?? ''] === attribute?.slug
            }]"
                    @click="onOptionClick(attributeGroup?.[0]?.attribute, attribute?.slug, attribute)"
                >
                    {{ attribute?.value }}
                </li>
                <div v-if="index === productAttributes.length - 1 && isCloseButtonVisible" @click="resetSelections">
                <a class="reset_variations" style="visibility: visible;">
                    <span class="close">+</span>
                </a>
            </div>
            </ul>
        </div>

        <input type="hidden" name="add-to-cart" :value="productId" />
        <input type="hidden" name="product_id" :value="productId" />
        <input type="hidden" name="variation_id" class="variation_id variable_variation_id" :value="selectedVariationId" />
    </div>
</template>

<script lang="ts" setup>
import { defineProps, ref, computed, onMounted } from 'vue';

interface Attribute {
    attribute: string;
    name: string;
    value: string;
    slug: string;
}
interface IVariationPrice{
    price: number,
    regularPrice: number,
    salePrice: number,
    short_term_data: {
        price: string,
        start_date: string,
        end_date: string,
    }
}

interface Variation {
    id: number;
    attrs: Record<string, string>;
    product: Product;
    isPreOrder: boolean;
    image: string;
    sku: string;
    variationPrice: IVariationPrice
}


interface ProductData {
    productAttributes: Attribute[][];
    availableVariations: Variation[];
    variations: any;
}

interface PostMeta {
    meta_id: string;
    post_id: string;
    meta_key: string;
    meta_value: string;
}

interface Product {
    PostMeta: PostMeta[];
}


// Define Props
const props = defineProps<{
    data: ProductData;
    productId: Number;
    engravingDesignStatus: string
}>();
const wooStore = useWooStore();
const productId = props.productId;
const { $ajax } = useNuxtApp();
const { trackCustom } = useCustomGtm();
const emit = defineEmits(['updatePrice', 'update-selection', 'update-preorder', 'quantity-elm', 'update-option','update-gallery-image','update-sku','update-description','variant-changed'])
// Extract product attributes and available variations from props
const variableData = computed(() => props.data);
const productAttributes = computed(() => variableData.value?.productAttributes || []);
const availableVariations = computed(() => variableData.value?.availableVariations || []);



// Use ref for selectedOptions so we can mutate it
const isEmited = ref(false);
const selectedOptions = computed(() => {
    const options: Record<string, string | null> = {};

    const { minVariationPrice, minIndex } = availableVariations.value.reduce((acc, variation, index) => {
        const variationPrice = parseFloat(variation.product.PostMeta.find(meta => (meta?.meta_key === '_price'))?.meta_value || '0');

        if (variationPrice < acc.minVariationPrice) {
            return { minVariationPrice: variationPrice, minIndex: index };
        }

        return acc;
    }, { minVariationPrice: Infinity, minIndex: -1 });

    const firstAvailableVariation = (minIndex !== -1) ? availableVariations.value[minIndex] : availableVariations.value[0];
    if (firstAvailableVariation) {
        const isPreOrder = firstAvailableVariation.isPreOrder;
        emit('update-preorder', isPreOrder);
        const selectedImage = firstAvailableVariation.image;
        if (selectedImage) {
            emit('update-gallery-image', selectedImage);
        }
        const selectedSku = firstAvailableVariation.sku;
        if (selectedSku) {
            emit('update-sku', selectedSku);
        }
        const _variation_description =  firstAvailableVariation.product.PostMeta.find(meta => meta.meta_key === '_variation_description')?.meta_value ?? null;
        if(_variation_description){
            emit('update-description', _variation_description);
        }else{
            emit('update-description', false);
        }
        if (!isEmited.value){
            const _variationPrice = firstAvailableVariation.variationPrice;
            const _variationSelectedPrice = {
                regular_price: _variationPrice.regularPrice.toString(),
                price: _variationPrice.price.toString(),
                sale_price: _variationPrice.salePrice ? _variationPrice.salePrice.toString() : null,
            };

            emit('updatePrice', _variationSelectedPrice);
            const quantity = firstAvailableVariation.product.PostMeta.find(meta => meta.meta_key === 'no_of_engraving_option')?.meta_value ?? null;
            if(quantity){
                emit('quantity-elm', quantity);
            }
            isEmited.value = true;

        }
        Object.entries(firstAvailableVariation.attrs).forEach(([key, value]) => {
            options[key.replace('attribute_', '')] = value;
        });
    }

    return options;
});

const mutableSelectedOptions = ref<Record<string, string | null>>({});
const selectedVariationId = ref<number | null>(0);
// Sync mutableSelectedOptions with selectedOptions on mount
onMounted(() => {
    // Delay initialization until availableVariations has data
    if (availableVariations.value.length > 0) {
        mutableSelectedOptions.value = { ...selectedOptions.value };

        // Update Variations ID when reload page
        const matchedVariation = availableVariations.value.find(variation => {
            return Object.keys(mutableSelectedOptions.value).every(key => {
                return variation.attrs[`attribute_${key}`] === mutableSelectedOptions.value[key];
            });
        });
        findMatchingVariation.value = matchedVariation ?? null;
        // Update selected variation ID based on matched variation
        selectedVariationId.value = findMatchingVariation.value ? findMatchingVariation.value.id : null;

        if( Object.keys(mutableSelectedOptions.value).length === Object.keys(productAttributes.value).length ) {
            const filteredItem = variableData.value.variations.find((item: { attrs: Record<string, string> }) =>
                Object.entries(mutableSelectedOptions.value).every(([key, value]) => item.attrs[key] === value)
            ) || null;
            if(filteredItem) {
                wooStore.setSingleStock(filteredItem.stock_status);
            }
        }
        // Update the price from the matching variation
        if (findMatchingVariation.value) {
            // Update Variation ID to hidden Input
            const variationID = findMatchingVariation.value.id;
            const variationEl = document.querySelector("form.cart input[name=variation_id]") as HTMLInputElement;
            if (variationEl){
                variationEl.value = variationID.toString();
            }
        }

    }
});

// Watch for changes in selectedOptions and reinitialize mutableSelectedOptions
watch(selectedOptions, (newOptions) => {
    mutableSelectedOptions.value = { ...newOptions };
});

// Function to check if an option should be disabled
const isDisabled = (attributeKey: string, attributeValue: string): boolean => {
    // Get currently selected attributes
    const selectedAttrs = { ...mutableSelectedOptions.value };

    // If no attributes are selected, nothing should be disabled
    const hasSelectedAttributes = Object.values(selectedAttrs).some(value => value !== null && value !== '');
    if (!hasSelectedAttributes) return false;

    // Find all variations that could potentially match the current selection
    const possibleVariations = availableVariations.value.filter(variation => {
        // For each selected attribute (except the current one we're checking)
        return Object.entries(selectedAttrs).every(([key, selectedValue]) => {
            // Skip the attribute we're currently checking
            if (key === attributeKey) return true;

            // If nothing is selected for this attribute, it's a match
            if (!selectedValue) return true;

            // Check if the variation matches the selected value
            const variationAttrKey = `attribute_${key}`;
            const variationValue = variation.attrs[variationAttrKey];

            // Empty strings in WooCommerce mean "any" value is acceptable
            return variationValue === "" || variationValue === selectedValue;
        });
    });

    // Check if this specific attribute value appears in any possible variations
    const isValueAvailable = possibleVariations.some(variation => {
        const variationAttrKey = `attribute_${attributeKey}`;
        const variationValue = variation.attrs[variationAttrKey];
        return variationValue === "" || variationValue === attributeValue;
    });

    // If the value doesn't appear in any possible variations, it should be disabled
    return !isValueAvailable;
};

const findMatchingVariation = ref<Variation | null>(null);

const selectedPrice = ref();

const updateSelectedVariationAndPrice = (attributeKey: string) => {
    const matchedVariation = availableVariations.value.find(variation => {
        // return Object.keys(mutableSelectedOptions.value).every(key => {
        //     return variation.attrs[`attribute_${key}`] === mutableSelectedOptions.value[key];
        // });
        return Object.keys(mutableSelectedOptions.value).every(key => {
            const selectedValue = mutableSelectedOptions.value[key];
            const attributeValue = variation.attrs[`attribute_${key}`];

            return attributeValue === "" || attributeValue === selectedValue;
        });
    });

    findMatchingVariation.value = matchedVariation ?? null;
    selectedVariationId.value = findMatchingVariation.value ? findMatchingVariation.value.id : null;


    if (findMatchingVariation.value) {
        const variationID = findMatchingVariation.value.id;

        wooStore.setSingleVariation(variationID);
        const variationEl = document.querySelector("form.cart input[name=variation_id]") as HTMLInputElement;
        if (variationEl){
            variationEl.value = variationID.toString();
        }
        const isPreOrder = findMatchingVariation.value.isPreOrder;
        emit('update-preorder', isPreOrder);

        const variationPrice = findMatchingVariation.value.variationPrice;
        selectedPrice.value = {
            regular_price: variationPrice.regularPrice,
            price: variationPrice.price,
            sale_price: variationPrice.salePrice,
        };
        // Handle Chain Option Price
        var chainPrice: number = 0;

        const chainEnabled = document.querySelector(".chain-options__chain-select") as HTMLSelectElement;
        
        const chainSelected = document.querySelector(".chain-options__select-chain.is_active_op") as HTMLElement;
        if (chainSelected) {
            
            const cPriceInfo = chainSelected.querySelector(".chain-options__product-price");
            if (cPriceInfo) {
                const price = parseFloat(cPriceInfo.getAttribute("data-price") || "0");
                const regularPrice = parseFloat(cPriceInfo.getAttribute("data-regular-price") || "0");
                if (!isNaN(price)) {
                    chainPrice = price;
                    selectedPrice.value = {
                        ...selectedPrice.value,
                        price: (parseFloat(selectedPrice.value.price) || 0) + chainPrice,
                        regular_price: (parseFloat(selectedPrice.value.regular_price) || 0) + regularPrice || 0,
                        sale_price: parseFloat(selectedPrice.value.sale_price || selectedPrice.value.price) + chainPrice,
                    };
                }

            }
        }
            
        
        // Prefer sale price if available
        const _variation_description =  findMatchingVariation.value.product.PostMeta.find(meta => meta.meta_key === '_variation_description')?.meta_value ?? null;
        if(_variation_description){
            emit('update-description', _variation_description);
        }else{
            emit('update-description', false);
        }
        const quantity = findMatchingVariation.value.product.PostMeta.find(meta => meta.meta_key === 'no_of_engraving_option')?.meta_value ?? null;
        if(quantity){
            emit('quantity-elm', quantity);
        }

        emit('update-selection', true);
        emit('update-option');
        emit('updatePrice', selectedPrice.value);

        const selectedImage = findMatchingVariation.value.image;
        if (selectedImage) {
            emit('update-gallery-image', selectedImage); // Emit event to update the gallery image
        }
        const selectedSku = findMatchingVariation.value.sku;
        if (selectedSku) {
            emit('update-sku', selectedSku);
            let gtmProductData = document.querySelector("input[name=gtm4wp_product_data]") as HTMLInputElement;
            if (gtmProductData){
                gtmProductData.setAttribute("data-sku", selectedSku);
                if (Object.values(findMatchingVariation.value.attrs).length > 0) {
                    const gtmAttributes = Object.values(findMatchingVariation.value.attrs);
                    const gtmVariant = gtmAttributes.join(", ");
                    gtmProductData.setAttribute("data-variant", gtmVariant);
                }
            }
        }
    } else {
        selectedPrice.value = null;
    }
};

const setDataLayer = async (productId: number) => {
    try {
        const response = await $ajax.get<{success: boolean; data: any}>(`wp-json/skj/v1/datalayer/single/${productId}`);
        if (response.success) {
            trackCustom(response.data);
        }
    } catch (error) {
        console.error('Failed to load user tracking data:', error)
    }
}

const onOptionClick = (attributeKey: string, attributeValue: string, attributes: any) => {
    updateVariationSelectDropdown(attributeKey, attributeValue);
    mutableSelectedOptions.value[attributeKey] = attributeValue;

    wooStore.setSingleVariation(0);
    // wooStore.setSingleStock('outofstock');

    if( Object.keys(mutableSelectedOptions.value).length === Object.keys(productAttributes.value).length ) {
        const filteredItem = variableData.value.variations.find((item: { attrs: Record<string, string> }) =>
            Object.entries(mutableSelectedOptions.value).every(([key, value]) => item.attrs[key] === value)
        ) || null;
        if(filteredItem) {
            wooStore.setSingleStock(filteredItem.stock_status);
            wooStore.setSingleVariation(filteredItem.variation_id);
        }

        setDataLayer(filteredItem.variation_id);
        //console.log('filteredItem', filteredItem);
    }
    updateSelectedVariationAndPrice(attributeKey);
     emit('variant-changed', mutableSelectedOptions.value);
};
const onOptionChange = (attribute: string) => {
    const selectedAttrs = mutableSelectedOptions.value;

    const matchedVariation = availableVariations.value.find(variation =>
        Object.keys(selectedAttrs).every(
        key => variation.attrs[`attribute_${key}`] === selectedAttrs[key]
        )
    );
    // Update selected variation ID based on matched variation
    selectedVariationId.value = matchedVariation ? matchedVariation.id : null;
};
// Select Options
const updateVariationSelectDropdown = (attributeKey: string, attributeSlug: string) => {
    const selectElement = document.querySelector(`form.cart select[name="attribute_${attributeKey}"]`) as HTMLSelectElement;
    if (selectElement) {
        selectElement.value = attributeSlug;
    }
}
// Reset selections for non-first attributes
const resetSelections = () => {
    // const keys = Object.keys(mutableSelectedOptions.value);
    // // Reset selected options for attributes after the given index
    // keys.forEach((key, i) => {
    //     mutableSelectedOptions.value[key] = null;
    // });
    mutableSelectedOptions.value = {};
    selectedVariationId.value = 0;
    emit('update-selection', false);
    emit('update-gallery-image', false);
    emit('update-sku', false)

    wooStore.setSingleStock('instock');
};
const selectedCount = computed(() => {
    return Object.values(mutableSelectedOptions.value).filter(v => v !== null).length;
});

// Computed property to determine if the close button should be visible
const isCloseButtonVisible = computed(() => {
    return selectedCount.value >= 1;
});
</script>
<style lang="scss">
    @import "@/assets/scss/components/product/_variable.scss";
</style>